import { Dispatch } from 'redux';

import { FormsConfigActions, selectQuestion } from 'app/store/data/formsConfig';
import { selectIsQuestionVisible } from 'app/store/selectors/isQuestionVisible';
import { RootState } from 'app/store/rootReducer';
import { FieldsEnum, FormsEnum } from 'app/shared/enums';
import { getMessageKeyWithSuffix } from 'app/services/validation.service';
import { isValidPhoneNumber } from 'app/validators/validatePhoneNumber';
import { PhoneNumbersService } from 'app/services/phoneNumbers.service';

const validatedPhoneNumbersCache = new Map<string, boolean>();

export function shouldValidatePhoneNumberWithBackend(
  formName: FormsEnum,
  questionKey: FieldsEnum,
  value?: string
) {
  const isPhoneNumberCheckoutField =
    [FormsEnum.CheckoutForm, FormsEnum.TravelCheckoutForm].includes(formName) &&
    [FieldsEnum.PhoneNumber, FieldsEnum.AdditionalPhoneNumber].includes(
      questionKey
    );

  if (!isPhoneNumberCheckoutField) {
    return false;
  }

  if (value === undefined) {
    return true;
  }

  return value !== '' && !validatedPhoneNumbersCache.has(value);
}

export async function validatePhoneNumberWithBackend(
  dispatch: Dispatch,
  getState: () => RootState,
  formName: FormsEnum,
  questionKey: string,
  rawValue: string | undefined
): Promise<boolean> {
  const state = getState();
  const question = selectQuestion({ state, formName, questionKey });

  const messageKey = getMessageKeyWithSuffix(
    questionKey,
    'backend_invalid_phone_number'
  );

  if (!question) {
    return true;
  }

  const visible = selectIsQuestionVisible({ state, formName, questionKey });
  const value = rawValue ?? '';
  const stringValue = value.toString();
  const shouldClearMessage =
    !visible ||
    question.disabled ||
    !value ||
    stringValue.includes('***') ||
    !isValidPhoneNumber(stringValue);

  if (shouldClearMessage) {
    dispatch(
      FormsConfigActions.setMessageVisibilityAction({
        formName,
        key: messageKey,
        visible: false,
      })
    );
    return true;
  }

  if (
    !shouldValidatePhoneNumberWithBackend(
      formName,
      questionKey as FieldsEnum,
      stringValue
    )
  ) {
    const cachedValidationResult = validatedPhoneNumbersCache.get(stringValue);
    if (cachedValidationResult === undefined) {
      return true;
    }
    dispatch(
      FormsConfigActions.setMessageVisibilityAction({
        formName,
        key: messageKey,
        visible: !cachedValidationResult,
      })
    );
    return cachedValidationResult;
  }

  try {
    const { data } = await PhoneNumbersService.validate(stringValue);
    const isValid = data !== false;
    validatedPhoneNumbersCache.set(stringValue, isValid);
    dispatch(
      FormsConfigActions.setMessageVisibilityAction({
        formName,
        key: messageKey,
        visible: !isValid,
      })
    );
    return isValid;
  } catch {
    dispatch(
      FormsConfigActions.setMessageVisibilityAction({
        formName,
        key: messageKey,
        visible: false,
      })
    );
    return true;
  }
}
